/**
 * Unified Authentication Service
 * Demonstrates the new service patterns in action
 * Replaces the legacy auth service with modern patterns
 */

import { BaseService } from "./core/base-service";
import { AuthenticationError, ValidationError } from "./core/errors";
import { serviceFactory, createServiceConfig } from "./core/service-factory";
import { ServiceConfig, ValidationSchema } from "./core/types";

// Auth-specific types
export interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  confirmPassword: string;
  termsAccepted: boolean;
}

export interface GoogleAuthData {
  access_token: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email?: string;
    phone?: string;
    name?: string;
    avatar?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
  permissions: string[];
}

export interface ForgotPasswordRequest {
  email?: string;
  phone?: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

/**
 * Modern authentication service with unified patterns
 */
export class UnifiedAuthService extends BaseService {
  constructor(config?: Partial<ServiceConfig>) {
    const serviceConfig = createServiceConfig("auth")
      .baseURL(process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:8000")
      .timeout(8000)
      .retries(2)
      .retryDelay(1000)
      .caching(false) // Auth requests should not be cached
      .logging(true)
      .metrics(true)
      .headers({
        "Content-Type": "application/json",
      })
      .credentials("include")
      .build();

    super({ ...serviceConfig, ...config });
  }

  /**
   * Validation schemas for auth operations
   */
  private static readonly schemas = {
    login: {
      rules: [
        { field: "password", required: true, type: "string", min: 6 },
        {
          field: "email",
          type: "string",
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          custom: (value: any, data: any) => {
            // Either email or phone is required
            return data.phone || value
              ? true
              : "Either email or phone is required";
          },
        },
        {
          field: "phone",
          type: "string",
          pattern: /^\+?[\d\s\-\(\)]+$/,
          custom: (value: any, data: any) => {
            // Either email or phone is required
            return data.email || value
              ? true
              : "Either email or phone is required";
          },
        },
      ],
      allowUnknown: false,
    } as ValidationSchema,

    register: {
      rules: [
        { field: "password", required: true, type: "string", min: 8 },
        {
          field: "confirmPassword",
          required: true,
          type: "string",
          custom: (value: any, data: any) => {
            return value === data.password ? true : "Passwords do not match";
          },
        },
        { field: "termsAccepted", required: true, type: "boolean" },
        {
          field: "email",
          type: "string",
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          custom: (value: any, data: any) => {
            return data.phone || value
              ? true
              : "Either email or phone is required";
          },
        },
        {
          field: "phone",
          type: "string",
          pattern: /^\+?[\d\s\-\(\)]+$/,
          custom: (value: any, data: any) => {
            return data.email || value
              ? true
              : "Either email or phone is required";
          },
        },
      ],
      allowUnknown: false,
    } as ValidationSchema,

    forgotPassword: {
      rules: [
        {
          field: "email",
          type: "string",
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          custom: (value: any, data: any) => {
            return data.phone || value
              ? true
              : "Either email or phone is required";
          },
        },
        {
          field: "phone",
          type: "string",
          pattern: /^\+?[\d\s\-\(\)]+$/,
          custom: (value: any, data: any) => {
            return data.email || value
              ? true
              : "Either email or phone is required";
          },
        },
      ],
      allowUnknown: false,
    } as ValidationSchema,

    resetPassword: {
      rules: [
        { field: "token", required: true, type: "string", min: 10 },
        { field: "password", required: true, type: "string", min: 8 },
        {
          field: "confirmPassword",
          required: true,
          type: "string",
          custom: (value: any, data: any) => {
            return value === data.password ? true : "Passwords do not match";
          },
        },
      ],
      allowUnknown: false,
    } as ValidationSchema,
  };

  /**
   * Login with email/phone and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Validate input
    this.validateSchema(credentials, UnifiedAuthService.schemas.login);

    // Determine login type
    const loginType = credentials.email ? "login" : "login";

    try {
      const response = await this.post<AuthResponse>(
        `/auth/${loginType}`,
        credentials,
        {
          timeout: 8000,
        },
      );

      this.logger.info("User logged in successfully", {
        userId: response.user.id,
        loginMethod: credentials.email ? "email" : "phone",
      });

      return response;
    } catch (error) {
      this.logger.warn("Login failed", {
        loginMethod: credentials.email ? "email" : "phone",
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Register new user account
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    // Validate input
    this.validateSchema(data, UnifiedAuthService.schemas.register);

    // Additional business logic validation
    if (!data.termsAccepted) {
      throw new ValidationError("Terms and conditions must be accepted", [
        {
          field: "termsAccepted",
          message: "Must accept terms and conditions",
          value: data.termsAccepted,
        },
      ]);
    }

    try {
      const response = await this.post<AuthResponse>(
        "/auth/register",
        {
          email: data.email,
          phone: data.phone,
          password: data.password,
          termsAccepted: data.termsAccepted,
        },
        {
          timeout: 8000,
        },
      );

      this.logger.info("User registered successfully", {
        userId: response.user.id,
        registrationMethod: data.email ? "email" : "phone",
      });

      return response;
    } catch (error) {
      this.logger.warn("Registration failed", {
        registrationMethod: data.email ? "email" : "phone",
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Google OAuth authentication
   */
  async loginWithGoogle(token: string): Promise<AuthResponse> {
    this.validateRequired({ access_token: token }, ["access_token"]);

    try {
      const response = await this.post<AuthResponse>(
        "/auth/google",
        {
          access_token: token,
        },
        {
          timeout: 8000,
        },
      );

      this.logger.info("Google login successful", {
        userId: response.user.id,
      });

      return response;
    } catch (error) {
      this.logger.warn("Google login failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Refresh authentication tokens
   */
  async refreshTokens(): Promise<{ accessToken: string; expiresIn: number }> {
    try {
      const response = await this.post<{
        accessToken: string;
        expiresIn: number;
      }>(
        "/auth/refresh",
        {},
        {
          timeout: 8000,
        },
      );

      this.logger.debug("Tokens refreshed successfully");
      return response;
    } catch (error) {
      this.logger.warn("Token refresh failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });

      // If refresh fails, it's likely an authentication issue
      throw new AuthenticationError("Failed to refresh authentication tokens", {
        details: error,
        service: this.name,
      });
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await this.post(
        "/auth/logout",
        {},
        {
          timeout: 8000,
        },
      );

      this.logger.info("User logged out successfully");
    } catch (error) {
      // Log error but don't throw - logout should always succeed from client perspective
      this.logger.warn("Logout request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Request password reset via email
   */
  async forgotPasswordEmail(email: string): Promise<void> {
    this.validateSchema(
      { email },
      {
        rules: [
          {
            field: "email",
            required: true,
            type: "string",
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          },
        ],
        allowUnknown: false,
      },
    );

    try {
      await this.post(
        "/auth/forgot-password/email",
        { email },
        {
          timeout: 8000,
        },
      );

      this.logger.info("Password reset email sent", { email });
    } catch (error) {
      this.logger.warn("Failed to send password reset email", {
        email,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Request password reset via phone
   */
  async forgotPasswordPhone(phone: string): Promise<void> {
    this.validateSchema(
      { phone },
      {
        rules: [
          {
            field: "phone",
            required: true,
            type: "string",
            pattern: /^\+?[\d\s\-\(\)]+$/,
          },
        ],
        allowUnknown: false,
      },
    );

    try {
      await this.post(
        "/auth/forgot-password/phone",
        { phone },
        {
          timeout: 8000,
        },
      );

      this.logger.info("Password reset SMS sent", { phone });
    } catch (error) {
      this.logger.warn("Failed to send password reset SMS", {
        phone,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    this.validateSchema(data, UnifiedAuthService.schemas.resetPassword);

    try {
      await this.patch(
        `/auth/reset-password/email?token=${encodeURIComponent(data.token)}`,
        {
          password: data.password,
        },
        {
          timeout: 8000,
        },
      );

      this.logger.info("Password reset successfully");
    } catch (error) {
      this.logger.warn("Password reset failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }

  /**
   * Get current user authentication status
   */
  async getAuthStatus(): Promise<{ authenticated: boolean; user?: any }> {
    try {
      const response = await this.get<{ authenticated: boolean; user?: any }>(
        "/auth/status",
        undefined,
        {
          timeout: 8000,
          cache: false, // Never cache auth status
        },
      );

      return response;
    } catch (error) {
      this.logger.debug("Auth status check failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });

      // Return unauthenticated status on any error
      return { authenticated: false };
    }
  }

  /**
   * Validate authentication token
   */
  async validateToken(token: string): Promise<boolean> {
    this.validateRequired({ token }, ["token"]);

    try {
      await this.post(
        "/auth/validate",
        { token },
        {
          timeout: 8000,
        },
      );

      return true;
    } catch (error) {
      this.logger.debug("Token validation failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return false;
    }
  }

  /**
   * Enhanced health check for auth service
   */
  async healthCheck(): Promise<{
    status: "healthy" | "unhealthy";
    details?: any;
  }> {
    try {
      // Check if auth endpoints are responsive
      const authStatus = await this.getAuthStatus();

      return {
        status: "healthy",
        details: {
          service: this.name,
          authEndpointResponsive: true,
          timestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      return {
        status: "unhealthy",
        details: {
          service: this.name,
          authEndpointResponsive: false,
          error: error instanceof Error ? error.message : "Unknown error",
          timestamp: new Date().toISOString(),
        },
      };
    }
  }
}

// Register the auth service with the factory
serviceFactory.registerConstructor("unified-auth", UnifiedAuthService);

// Create and register a default instance
export const unifiedAuthService = new UnifiedAuthService();
serviceFactory.register(unifiedAuthService);

// Export for backward compatibility
export default unifiedAuthService;
